import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileText, Users, MapPin, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function firstOfMonthISO(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function lastMonthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}
function currentMonthRange() {
  return { from: firstOfMonthISO(), to: todayISO() };
}

function PresetBar({ value, onChange }) {
  const presets = [
    { key: 'current', label: 'Current Month' },
    { key: 'last', label: 'Last Month' },
    { key: 'custom', label: 'Custom' },
  ];
  return (
    <div className="flex gap-1.5" data-testid="report-preset-bar">
      {presets.map(p => (
        <Button
          key={p.key}
          size="sm"
          variant={value === p.key ? 'default' : 'outline'}
          className={`h-7 text-xs ${value === p.key ? 'bg-[#0D2850] hover:bg-[#0B2246]' : ''}`}
          onClick={() => onChange(p.key)}
          data-testid={`preset-${p.key}`}
        >
          {p.label}
        </Button>
      ))}
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function ReportCard({ title, icon: Icon, description, onDownload, employees, showEmployeeFilter, showClientFilter, color, testIdPrefix }) {
  const cur = currentMonthRange();
  const [preset, setPreset] = useState('current');
  const [fromDate, setFromDate] = useState(cur.from);
  const [toDate, setToDate] = useState(cur.to);
  const [employeeId, setEmployeeId] = useState('');
  const [clientName, setClientName] = useState('');
  const [loadingFmt, setLoadingFmt] = useState(null);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === 'current') {
      const r = currentMonthRange();
      setFromDate(r.from); setToDate(r.to);
    } else if (key === 'last') {
      const r = lastMonthRange();
      setFromDate(r.from); setToDate(r.to);
    }
  };

  const handleDownload = async (format) => {
    if (!fromDate || !toDate) {
      toast.error('Please select a date range');
      return;
    }
    if (fromDate > toDate) {
      toast.error('From date must be before To date');
      return;
    }
    setLoadingFmt(format);
    try {
      await onDownload({ from_date: fromDate, to_date: toDate, employee_id: employeeId, client_name: clientName, format });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download report');
    }
    setLoadingFmt(null);
  };

  return (
    <Card className="border-slate-200" data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold text-slate-800">{title}</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <PresetBar value={preset} onChange={applyPreset} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">From</Label>
            <Input
              type="date" value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPreset('custom'); }}
              className="h-8 text-xs"
              data-testid={`${testIdPrefix}-from`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">To</Label>
            <Input
              type="date" value={toDate}
              onChange={e => { setToDate(e.target.value); setPreset('custom'); }}
              className="h-8 text-xs"
              data-testid={`${testIdPrefix}-to`}
            />
          </div>
        </div>
        {showEmployeeFilter && (
          <div>
            <Label className="text-[11px] text-slate-500">Salesperson (Optional)</Label>
            <Select value={employeeId || 'all'} onValueChange={v => setEmployeeId(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-employee`}>
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {(employees || []).map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {showClientFilter && (
          <div>
            <Label className="text-[11px] text-slate-500">Client Name (Optional)</Label>
            <Input
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              placeholder="Filter by client name..."
              className="h-8 text-xs"
              data-testid={`${testIdPrefix}-client`}
            />
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            disabled={loadingFmt !== null}
            onClick={() => handleDownload('csv')}
            className="flex-1 h-8 text-xs"
            data-testid={`${testIdPrefix}-csv-btn`}
          >
            {loadingFmt === 'csv' ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Download className="w-3 h-3 mr-1.5" />}
            Download CSV
          </Button>
          <Button
            size="sm"
            disabled={loadingFmt !== null}
            onClick={() => handleDownload('pdf')}
            className="flex-1 h-8 text-xs bg-[#0D2850] hover:bg-[#0B2246]"
            data-testid={`${testIdPrefix}-pdf-btn`}
          >
            {loadingFmt === 'pdf' ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <FileText className="w-3 h-3 mr-1.5" />}
            Download PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function JourneyReportsTab({ employees }) {
  // ─── Total Expense Report ──────────────────────────────
  const downloadTotalExpense = async ({ from_date, to_date, employee_id, format }) => {
    const res = await api.get('/api/journey/reports/total-expense', {
      params: { from_date, to_date, ...(employee_id ? { employee_id } : {}), format },
      responseType: 'blob',
    });
    const ext = format === 'pdf' ? 'pdf' : 'csv';
    downloadBlob(res.data, `total_expense_${from_date}_to_${to_date}.${ext}`);
    toast.success(`Downloaded ${ext.toUpperCase()}`);
  };

  // ─── Salesperson Visit Summary ─────────────────────────
  const downloadSalespersonSummary = async ({ from_date, to_date, format }) => {
    const res = await api.get('/api/journey/reports/salesperson-summary', {
      params: { from_date, to_date, format },
      responseType: 'blob',
    });
    const ext = format === 'pdf' ? 'pdf' : 'csv';
    downloadBlob(res.data, `salesperson_summary_${from_date}_to_${to_date}.${ext}`);
    toast.success(`Downloaded ${ext.toUpperCase()}`);
  };

  // ─── Detailed Visit Log ────────────────────────────────
  const downloadVisitsDetailed = async ({ from_date, to_date, employee_id, client_name, format }) => {
    const res = await api.get('/api/journey/reports/visits-detailed', {
      params: {
        from_date, to_date,
        ...(employee_id ? { employee_id } : {}),
        ...(client_name ? { client_name } : {}),
        format,
      },
      responseType: 'blob',
    });
    const ext = format === 'pdf' ? 'pdf' : 'csv';
    downloadBlob(res.data, `visits_detailed_${from_date}_to_${to_date}.${ext}`);
    toast.success(`Downloaded ${ext.toUpperCase()}`);
  };

  return (
    <div className="space-y-4" data-testid="journey-reports-tab">
      <div className="bg-[#0D2850]/5 border border-[#0D2850]/15 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-[#0D2850] flex items-center gap-2">
          <FileText className="w-4 h-4" /> Journey Reports
        </h3>
        <p className="text-xs text-slate-600 mt-1">
          Download CSV or PDF reports filtered by Current Month, Last Month, or a custom date range.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ReportCard
          title="Total Expense Report"
          description="All filed expenses with item-level breakdown."
          icon={IndianRupee}
          color="bg-emerald-600"
          employees={employees}
          showEmployeeFilter={true}
          showClientFilter={false}
          onDownload={downloadTotalExpense}
          testIdPrefix="report-total-expense"
        />
        <ReportCard
          title="Salesperson Visit Summary"
          description="Per-salesperson roll-up: visits, clients, plans, expenses."
          icon={Users}
          color="bg-indigo-600"
          employees={employees}
          showEmployeeFilter={false}
          showClientFilter={false}
          onDownload={downloadSalespersonSummary}
          testIdPrefix="report-salesperson"
        />
        <ReportCard
          title="Detailed Visit Log"
          description="Every visit with employee, client, GPS, purpose, remarks."
          icon={MapPin}
          color="bg-amber-600"
          employees={employees}
          showEmployeeFilter={true}
          showClientFilter={true}
          onDownload={downloadVisitsDetailed}
          testIdPrefix="report-visits-detailed"
        />
      </div>
    </div>
  );
}
